import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useState,
} from "react";

import { css } from "@emotion/react";
import {
  LockFill,
  LockOpenFill,
  CloseXSm,
} from "@hl/base-components/lib/assets/icons.generated";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import {
  Group,
  Text,
  Select,
  SelectProps,
  Space,
  ActionIcon,
  Card,
  Stack,
  Box,
  createStyles,
  Button,
  Flex,
} from "@mantine/core";
import { IconPencil } from "@tabler/icons";

import GateEditModal from "components/GateEditModal";
import { ReactComponent as CreateIcon } from "~/assets/icons/round-plus-sign-dark.svg";
import { CreateOrEditGate } from "~/features/gates/CreateOrEditGate";

const iconStyle = css`
  width: 20px;
  height: 20px;
`;

type IconType = "locked" | "unlocked" | "create";

interface Item {
  icon: IconType;
  label: string;
  value: string;
  description?: string;
  onClick?(): void;
}

type ItemProps = Item & ComponentPropsWithoutRef<"div">;

const noGateItemForPageCreation: Item = {
  icon: "unlocked",
  label: "No gate",
  value: "",
  description: "Anyone can view this page",
};

const noGateItemForMintCreation: Item = {
  icon: "unlocked",
  label: "No gate",
  value: "",
  description: "Anyone can mint this token",
};

const noGateItemForAuctionCreation: Item = {
  icon: "unlocked",
  label: "No gate",
  value: "",
  description: "Anyone can bid on this token",
};

const iconComponent = (icon: IconType) => {
  switch (icon) {
    case "create":
      return <CreateIcon css={iconStyle} />;
    case "locked":
      return <LockFill css={iconStyle} />;
    case "unlocked":
      return <LockOpenFill css={iconStyle} />;
  }
};

const useStyles = createStyles({
  selectItem: {
    paddingTop: 4,
    paddingBottom: 4,
    button: {
      display: "none",
    },
  },
  selectItemButton: {
    "&:hover": {
      button: {
        display: "block",
      },
    },
  },
});

const SelectItem = forwardRef<
  HTMLDivElement,
  ItemProps & { customChange?: (gateId?: string) => void }
>(({ icon, label, description, customChange, ...others }, ref) => {
  const { value } = others;
  const { pushModal, popModal } = useModalStack();
  const { classes, cx } = useStyles();

  return (
    <Flex
      ref={ref}
      {...others}
      gap="xs"
      className={cx([
        others.className,
        classes.selectItem,
        value ? classes.selectItemButton : undefined,
      ])}
    >
      <Group noWrap spacing={8} py={4}>
        {iconComponent(icon)}

        <div>
          <Text size="sm">{label}</Text>
          <Text size="xs" opacity={0.6}>
            {description}
          </Text>
        </div>
      </Group>
      <Button
        ml="auto"
        size="xs"
        leftIcon={<IconPencil size={16} />}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          pushModal(
            <GateEditModal
              gateId={value}
              onSave={async (gateId) => {
                customChange?.(gateId);
                popModal();
              }}
            />,
            {
              title: (
                <Box mr={"-md"}>
                  <Group position="apart">
                    <Text size="lg" weight={500}>
                      Edit a gate
                    </Text>
                    <ActionIcon onClick={popModal}>
                      <CloseXSm />
                    </ActionIcon>
                  </Group>
                  <Space h={4} />
                  <Text size="sm">
                    Gates are rules you set that determine who can and
                    can&apos;t access specific content. To pass a gate, someone
                    must meet the conditions you specify below.
                  </Text>
                  <Space h="sm" />
                </Box>
              ),
              withCloseButton: false,
              size: "xl",
              onClose: () => {
                popModal();
              },
            }
          );
        }}
      >
        Edit gate
      </Button>
    </Flex>
  );
});

export interface SelectableGate {
  id: string;
  name: string | null;
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
type GateSelectProps = {
  gates: SelectableGate[];
  value: string;
  onChange: any;
  isGatingMint?: boolean;
  isGatingAuction?: boolean;
} & Omit<SelectProps, "data">;

export const GateSelectOnCreate = ({
  isDirectMint,
  ...props
}: GateSelectProps & { isDirectMint: boolean }) => {
  if (isDirectMint) {
    return (
      <Stack spacing={0}>
        <Text size="sm" fw={500}>
          Gate
        </Text>
        <Text size="sm" color={TEXT_COLOR.SECONDARY}>
          {props.description}
        </Text>

        <Card p="xs" mt={8}>
          <Group align="center" noWrap pl={8}>
            <LockOpenFill />
            <Stack spacing={0}>
              <Text size="sm">No gate selected</Text>
              <Text size="xs" color={TEXT_COLOR.SECONDARY}>
                Disable direct minting to add a gate
              </Text>
            </Stack>
          </Group>
        </Card>
      </Stack>
    );
  }
  return <GateSelect {...props} />;
};

const GateSelect = ({
  gates,
  value,
  onChange,
  isGatingMint,
  isGatingAuction = false,
  ...rest
}: GateSelectProps) => {
  const { pushModal, popModal } = useModalStack();
  const [currentValue, setCurrentValue] = useState<string | null>();

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const gatesData = [
    isGatingAuction
      ? noGateItemForAuctionCreation
      : isGatingMint
      ? noGateItemForMintCreation
      : noGateItemForPageCreation,
    ...gates.map((gate) => ({
      icon: "locked",
      label: gate.name || gate.id,
      value: gate.id,
    })),
  ];

  const updateGate = (gateId?: string) => {
    setCurrentValue(gateId);
    onChange(gateId);
  };

  return (
    <Select
      label="Gate"
      placeholder="No gate selected"
      value={currentValue}
      onChange={(gateId) => {
        setCurrentValue(gateId);
        onChange(gateId ?? "");
      }}
      icon={currentValue ? iconComponent("locked") : iconComponent("unlocked")}
      itemComponent={(data) => (
        <SelectItem {...data} customChange={updateGate} />
      )}
      creatable
      shouldCreate={() => true}
      getCreateLabel={() => (
        <SelectItem icon="create" label="Create a new gate" value="" />
      )}
      styles={(theme) => ({
        dropdown: {
          outline: "none",
          border: "none",
        },
        description: {
          fontSize: theme.fontSizes.sm,
          color: theme.colors.primaryText[0],
        },
      })}
      onCreate={() => {
        pushModal(
          <CreateOrEditGate
            onSave={async (gateId) => {
              setCurrentValue(gateId);
              onChange(gateId);
              popModal();
            }}
            isModal
          />,
          {
            title: (
              <Box mr={"-md"}>
                <Group position="apart">
                  <Text size="lg" weight={500}>
                    Create a gate
                  </Text>
                  <ActionIcon onClick={popModal}>
                    <CloseXSm />
                  </ActionIcon>
                </Group>
                <Space h={4} />
                <Text size="sm">
                  Gates are rules you set that determine who can and can&apos;t
                  access specific content. To pass a gate, someone must meet the
                  conditions you specify below.
                </Text>
                <Space h="sm" />
              </Box>
            ),
            withCloseButton: false,
            size: "xl",
          }
        );
        return undefined;
      }}
      {...rest}
      data={gatesData}
    />
  );
};

export default GateSelect;
